import { useEffect, useRef } from "react";
import L from "leaflet";

const SENEGAL_CENTER = [14.6, -14.4];
const SENEGAL_ZOOM = 6;

const buildIcon = (alert, selected) => {
  const assigned = alert.assigned_to ? "assigned" : "";
  const sel = selected ? "selected" : "";
  return L.divIcon({
    className: "",
    html: `<div class="sos-marker ${assigned} ${sel}"><div class="ring"></div><div class="dot"></div></div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });
};

const MapView = ({ alerts, selectedAlert, onSelect, agentTrack }) => {
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const markersRef = useRef({});
  const polylineRef = useRef(null);
  const startMarkerRef = useRef(null);
  const didFitRef = useRef(false);
  const lastFocusRef = useRef(null);
  const agentPolyRef = useRef(null);
  const agentMarkerRef = useRef(null);

  // init map once
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = L.map(containerRef.current, {
      center: SENEGAL_CENTER,
      zoom: SENEGAL_ZOOM,
      minZoom: 5,
      zoomControl: true,
      attributionControl: true,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
      subdomains: "abc",
      className: "osm-dark-tiles",
    }).addTo(map);
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 200);
    return () => {
      try { map.stop(); } catch (e) { /* no-op */ }
      map.remove();
      mapRef.current = null;
      markersRef.current = {};
      polylineRef.current = null;
      startMarkerRef.current = null;
      agentPolyRef.current = null;
      agentMarkerRef.current = null;
    };
  }, []);

  // sync markers
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const ids = new Set(alerts.map((a) => a.alert_id));

    // remove stale
    Object.keys(markersRef.current).forEach((id) => {
      if (!ids.has(id)) {
        map.removeLayer(markersRef.current[id]);
        delete markersRef.current[id];
      }
    });

    alerts.forEach((a) => {
      const loc = a.current_location;
      if (!loc) return;
      const selected = selectedAlert?.alert_id === a.alert_id;
      const icon = buildIcon(a, selected);
      if (markersRef.current[a.alert_id]) {
        const m = markersRef.current[a.alert_id];
        m.setLatLng([loc.lat, loc.lng]);
        m.setIcon(icon);
      } else {
        const m = L.marker([loc.lat, loc.lng], { icon }).addTo(map);
        m.on("click", () => onSelect(a));
        m.bindTooltip(a.user_name, {
          direction: "top",
          offset: [0, -12],
          className: "sj-tooltip",
        });
        markersRef.current[a.alert_id] = m;
      }
    });

    // Keep the default Senegal view; only auto-fit if alerts span a wide area.
    if (!didFitRef.current && alerts.length > 1) {
      const pts = alerts
        .filter((a) => a.current_location)
        .map((a) => [a.current_location.lat, a.current_location.lng]);
      if (pts.length > 1) {
        const b = L.latLngBounds(pts);
        const spanOk =
          b.getNorth() - b.getSouth() < 4 && b.getEast() - b.getWest() < 4;
        if (spanOk) map.fitBounds(b.pad(0.4), { maxZoom: 12, minZoom: 6 });
        didFitRef.current = true;
      }
    }
  }, [alerts, selectedAlert, onSelect]);

  // draw green route of selected alert
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (polylineRef.current) { map.removeLayer(polylineRef.current); polylineRef.current = null; }
    if (startMarkerRef.current) { map.removeLayer(startMarkerRef.current); startMarkerRef.current = null; }

    if (selectedAlert) {
      const locs = (selectedAlert.locations || []).filter((l) => l?.lat != null);
      if (locs.length > 1) {
        const pts = locs.map((l) => [l.lat, l.lng]);
        polylineRef.current = L.polyline(pts, {
          color: "#EF4444",
          weight: 4,
          opacity: 0.95,
          dashArray: "1,8",
          lineCap: "round",
        }).addTo(map);
        const s = locs[0];
        startMarkerRef.current = L.marker([s.lat, s.lng], {
          icon: L.divIcon({ className: "", html: '<div class="path-start-marker"></div>', iconSize: [14, 14], iconAnchor: [7, 7] }),
        }).addTo(map).bindTooltip("Départ", { direction: "top", offset: [0, -8] });
      }
      const loc = selectedAlert.current_location;
      // Only re-center when a different alert becomes selected (avoid fighting polling updates)
      if (loc && lastFocusRef.current !== selectedAlert.alert_id) {
        map.flyTo([loc.lat, loc.lng], Math.max(map.getZoom(), 15), { duration: 0.9 });
        lastFocusRef.current = selectedAlert.alert_id;
      }
    } else {
      lastFocusRef.current = null;
    }
  }, [selectedAlert]);

  // draw agent (intervention) track in blue + agent marker
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (agentPolyRef.current) { map.removeLayer(agentPolyRef.current); agentPolyRef.current = null; }
    if (agentMarkerRef.current) { map.removeLayer(agentMarkerRef.current); agentMarkerRef.current = null; }

    if (agentTrack && agentTrack.current) {
      const pts = (agentTrack.points || []).filter((p) => p && p.length === 2);
      if (pts.length > 1) {
        agentPolyRef.current = L.polyline(pts, {
          color: "#2563EB",
          weight: 4,
          opacity: 0.9,
          dashArray: "6,6",
          lineCap: "round",
        }).addTo(map);
      }
      const arrived = agentTrack.arrived ? "arrived" : "";
      agentMarkerRef.current = L.marker(agentTrack.current, {
        icon: L.divIcon({
          className: "",
          html: `<div class="agent-marker ${arrived}"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/><circle cx="7" cy="17" r="2"/><circle cx="17" cy="17" r="2"/></svg></div>`,
          iconSize: [26, 26],
          iconAnchor: [13, 13],
        }),
      }).addTo(map).bindTooltip(`${agentTrack.name}${agentTrack.arrived ? " — arrivé" : " — en route"}`, { direction: "top", offset: [0, -14] });
    }
  }, [agentTrack]);

  return <div ref={containerRef} data-testid="sos-map" className="w-full h-full" />;
};

export default MapView;
